/* Halvari interface: Auto / Light / Dark switch, header scroll state, menu and search drawers,
   product suggestions in the search panel. Vanilla JS, no dependencies.
   Everything uses event delegation on document, so it keeps working after the
   Theme Editor re-renders a section. The cart drawer has its own file (cart.js). */
(function () {
  'use strict';

  if (window.__halvariThemeInit) return;
  window.__halvariThemeInit = true;

  var root = document.documentElement;
  var routes = window.halvariRoutes || {};
  var themeApi = window.halvariTheme || null; /* set by snippets/theme-init.liquid before first paint */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function noop() {}

  /* ---------- Auto / Light / Dark ----------
     The mode ("auto", "light" or "dark") lives on <html data-theme-mode>; the theme it resolves
     to lives on <html data-theme>. The sunrise / sunset logic is in snippets/theme-init.liquid
     and is reused here through window.halvariTheme, so there is one theme system, not two. */

  var autoTimer = null;

  function currentTheme() {
    return root.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  }

  function currentMode() {
    var mode = root.getAttribute('data-theme-mode');
    return mode === 'light' || mode === 'dark' ? mode : 'auto';
  }

  function resolveTheme(mode) {
    if (mode === 'light' || mode === 'dark') return mode;
    return themeApi && themeApi.resolve ? themeApi.resolve('auto') : currentTheme();
  }

  function syncThemeControls() {
    var mode = currentMode();
    qsa('[data-theme-set]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-theme-set') === mode ? 'true' : 'false');
    });
    var meta = document.querySelector('meta[name="theme-color"]');
    var background = window.getComputedStyle(root).getPropertyValue('--color-bg').trim();
    if (meta && background) meta.setAttribute('content', background);
  }

  function applyTheme(theme) {
    if (theme === currentTheme()) return;
    root.classList.add('theme-transition');
    window.setTimeout(function () {
      root.classList.remove('theme-transition');
    }, 500);
    root.setAttribute('data-theme', theme);
  }

  /* While AUTO is active, look again at the next sunrise or sunset. One timer, no polling. */
  function scheduleAuto() {
    window.clearTimeout(autoTimer);
    if (currentMode() !== 'auto' || !themeApi || !themeApi.nextChange) return;
    var wait = themeApi.nextChange() - Date.now() + 1000;
    autoTimer = window.setTimeout(refreshAuto, Math.min(Math.max(wait, 1000), 21600000));
  }

  function refreshAuto() {
    if (currentMode() === 'auto') {
      applyTheme(resolveTheme('auto'));
      syncThemeControls();
    }
    scheduleAuto();
  }

  function setMode(mode, remember) {
    if (mode !== 'auto' && mode !== 'light' && mode !== 'dark') return;
    root.setAttribute('data-theme-mode', mode);
    applyTheme(resolveTheme(mode));
    if (remember && themeApi && themeApi.save) themeApi.save(mode);
    syncThemeControls();
    scheduleAuto();
  }

  /* ---------- Header ---------- */

  var scrollTicking = false;

  function updateHeader() {
    scrollTicking = false;
    var header = document.querySelector('[data-header]');
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 12);
  }

  window.addEventListener(
    'scroll',
    function () {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(updateHeader);
    },
    { passive: true }
  );

  /* ---------- Fit text ----------
     Sizes a single line (the footer name) so it spans its container exactly, whatever the name is. */

  function fitText() {
    qsa('[data-fit-text]').forEach(function (node) {
      var box = node.parentElement;
      if (!box) return;
      var style = window.getComputedStyle(box);
      var room = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      node.style.fontSize = '100px';
      var width = node.getBoundingClientRect().width;
      if (!width || room <= 0) return;
      node.style.fontSize = (100 * room) / width + 'px';
    });
  }

  var fitTicking = false;

  window.addEventListener('resize', function () {
    if (fitTicking) return;
    fitTicking = true;
    window.requestAnimationFrame(function () {
      fitTicking = false;
      fitText();
    });
  });

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitText);

  /* ---------- Drawers (menu, search) ---------- */

  var activeDrawer = null;
  var lastTrigger = null;

  function panelOf(drawer) {
    return drawer.querySelector('[data-drawer-panel]');
  }

  function setExpanded(drawer, open) {
    qsa('[data-drawer-open="' + drawer.id + '"]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function closeDrawer(restoreFocus) {
    var drawer = activeDrawer;
    if (!drawer) return;
    activeDrawer = null;
    drawer.classList.remove('is-open');
    root.classList.remove('is-locked');
    setExpanded(drawer, false);
    if (restoreFocus !== false && lastTrigger && lastTrigger.focus && document.contains(lastTrigger)) {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  function openDrawer(id, trigger) {
    var drawer = document.getElementById(id);
    if (!drawer) return false;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(false);
    lastTrigger = trigger || document.activeElement;
    activeDrawer = drawer;
    drawer.classList.add('is-open');
    root.classList.add('is-locked');
    setExpanded(drawer, true);
    var target = drawer.querySelector('[data-autofocus]') || panelOf(drawer);
    if (target) target.focus({ preventScroll: true });
    return true;
  }

  function visibleFocusable(container) {
    return qsa(FOCUSABLE, container).filter(function (node) {
      return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
    });
  }

  function trapFocus(event) {
    var panel = panelOf(activeDrawer);
    if (!panel) return;
    var items = visibleFocusable(panel);
    if (!items.length) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var current = document.activeElement;
    if (event.shiftKey && (current === first || current === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    } else if (!panel.contains(current)) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- Header search: the field slides in over the wordmark ---------- */

  var searchOpen = false;

  function searchParts() {
    var header = document.querySelector('[data-header]');
    var form = header && header.querySelector('[data-search-form]');
    return form ? { header: header, form: form } : null;
  }

  /* Keep the field between the menu button and the cart. */
  function placeSearch(header) {
    var bar = header.querySelector('.header__bar');
    var menu = header.querySelector('.header__menu');
    var cart = header.querySelector('.header__cart');
    if (!bar || !menu || !cart) return;
    var b = bar.getBoundingClientRect();
    header.style.setProperty('--search-left', Math.round(menu.getBoundingClientRect().right - b.left + 8) + 'px');
    header.style.setProperty('--search-right', Math.round(b.right - cart.getBoundingClientRect().left + 8) + 'px');
  }

  function setSearch(open, restoreFocus) {
    var parts = searchParts();
    if (!parts) return false;
    searchOpen = open;
    if (open) {
      if (activeDrawer) closeDrawer(false);
      placeSearch(parts.header);
    }
    parts.header.classList.toggle('is-search-open', open);
    qsa('.header__search', parts.header).forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    var input = parts.form.querySelector('input[type="search"]');
    if (open) {
      window.requestAnimationFrame(function () {
        if (input) input.focus({ preventScroll: true });
      });
    } else {
      if (searchRequest && searchRequest.abort) searchRequest.abort();
      clearSuggestions();
      if (restoreFocus !== false) {
        var trigger = parts.header.querySelector('.header__search');
        if (trigger && trigger.focus) trigger.focus({ preventScroll: true });
      }
    }
    return true;
  }

  /* ---------- Search suggestions (Shopify predictive search) ---------- */

  var searchTimer = null;
  var searchRequest = null;

  function clearSuggestions() {
    var box = document.querySelector('[data-predictive-results]');
    if (box) box.innerHTML = '';
  }

  function loadSuggestions(terms) {
    var box = document.querySelector('[data-predictive-results]');
    if (!box) return;
    if (searchRequest && searchRequest.abort) searchRequest.abort();
    searchRequest = 'AbortController' in window ? new AbortController() : null;

    var url =
      (routes.predictiveSearch || '/search/suggest') +
      '?q=' +
      encodeURIComponent(terms) +
      '&resources%5Btype%5D=product&resources%5Blimit%5D=4&section_id=predictive-search';

    fetch(url, { credentials: 'same-origin', signal: searchRequest ? searchRequest.signal : undefined })
      .then(function (response) {
        if (!response.ok) throw new Error('search');
        return response.text();
      })
      .then(function (html) {
        var source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-content]');
        box.innerHTML = source ? source.innerHTML : '';
      })
      .catch(noop); /* suggestions are optional: pressing Enter still opens the search page */
  }

  /* ---------- Events ---------- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var themeButton = target.closest('[data-theme-set]');
    if (themeButton) {
      setMode(themeButton.getAttribute('data-theme-set'), true);
      return;
    }

    var searchToggle = target.closest('[data-search-toggle]');
    if (searchToggle) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (setSearch(!searchOpen)) event.preventDefault();
      return;
    }

    if (searchOpen && !target.closest('[data-header]')) {
      setSearch(false, false);
    }

    var opener = target.closest('[data-drawer-open]');
    if (opener) {
      /* Let modified clicks (new tab) behave like the plain links these are. */
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var id = opener.getAttribute('data-drawer-open');
      if (activeDrawer && activeDrawer.id === id) {
        event.preventDefault();
        closeDrawer();
      } else if (openDrawer(id, opener)) {
        event.preventDefault();
      }
      return;
    }

    if (activeDrawer && target.closest('[data-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (event) {
    var target = event.target;

    /* Links that act as buttons must also answer to Space. */
    if (
      (event.key === ' ' || event.key === 'Spacebar') &&
      target &&
      target.matches &&
      target.matches('a[role="button"]')
    ) {
      event.preventDefault();
      target.click();
      return;
    }

    if (searchOpen && event.key === 'Escape') {
      event.preventDefault();
      setSearch(false);
      return;
    }

    if (!activeDrawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
    } else if (event.key === 'Tab') {
      trapFocus(event);
    }
  });

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-predictive-input]')) return;
    var terms = input.value.trim();
    window.clearTimeout(searchTimer);
    if (terms.length < 2) {
      if (searchRequest && searchRequest.abort) searchRequest.abort();
      clearSuggestions();
      return;
    }
    searchTimer = window.setTimeout(function () {
      loadSuggestions(terms);
    }, 220);
  });

  window.addEventListener('resize', function () {
    var parts = searchParts();
    if (searchOpen && parts) placeSearch(parts.header);
  });

  /* A sleeping tab can miss its timer: check again whenever the page comes back into view. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshAuto();
  });

  /* The choice made in another tab applies here too. */
  window.addEventListener('storage', function (event) {
    if (!themeApi || event.key !== themeApi.key || !event.newValue) return;
    setMode(event.newValue, false);
  });

  /* Theme Editor: a re-rendered header or footer needs its controls synced again. */
  document.addEventListener('shopify:section:load', function () {
    searchOpen = false;
    if (activeDrawer && !document.contains(activeDrawer)) {
      activeDrawer = null;
      lastTrigger = null;
      root.classList.remove('is-locked');
    }
    syncThemeControls();
    updateHeader();
    fitText();
  });

  /* Back/forward cache can restore a page with a drawer left open. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      closeDrawer(false);
      setSearch(false, false);
      refreshAuto();
    }
  });

  syncThemeControls();
  scheduleAuto();
  updateHeader();
  fitText();
})();
